import React from "react";
import styled from "styled-components";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import CreditItemRow from "./CreditItemRow";
import type { CreditItem } from "../../app/services/credits";

const ListContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export interface CreditListProps {
  items: CreditItem[];
  canEdit: boolean;
  language: "JP" | "EN";
  onReorder: (orderedIds: string[]) => void;
  onAddSectionAbove: (item: CreditItem) => void;
  onAddSubsectionAbove: (item: CreditItem) => void;
  onEdit: (item: CreditItem) => void;
  onDelete: (item: CreditItem) => void;
}

const CreditList: React.FC<CreditListProps> = ({
  items,
  canEdit,
  language,
  onReorder,
  onAddSectionAbove,
  onAddSubsectionAbove,
  onEdit,
  onDelete,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((i) => i.item_id === active.id);
    const newIndex = items.findIndex((i) => i.item_id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(items, oldIndex, newIndex);
    onReorder(reordered.map((i) => i.item_id));
  };

  return (
    <ListContainer>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((i) => i.item_id)}
          strategy={verticalListSortingStrategy}
        >
          {items.map((item) => (
            <CreditItemRow
              key={item.item_id}
              item={item}
              canEdit={canEdit}
              language={language}
              onAddSectionAbove={onAddSectionAbove}
              onAddSubsectionAbove={onAddSubsectionAbove}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </SortableContext>
      </DndContext>
    </ListContainer>
  );
};

export default CreditList;
